"use client";
import SectionTitle from "@/components/shared/sectionTitle";
import { Separator } from "@/components/ui/separator";
import Image from "next/image";
import { cn, getFilePath } from "@/lib/utils/utils";
import Banner from "@/components/shared/banner";
import useLocalizer from "@/hooks/useLocalizer";
import { Activity } from "react";
import Flex from "@/components/core/flex";
import GalleryContainer from "@/components/shared/galleryContainer";
import ContactInfoBox from "@/components/shared/contactInfoBox";
import { ClientProjectDetailsResponse } from "@/types/project";

interface Props {
  className?: string;
  data: ClientProjectDetailsResponse;
}

const ProjectDetailsContent = ({ data, className }: Props) => {
  const clientProjectDetails = data;
  const { isRtl, t } = useLocalizer();
  return (
    <section className={cn("relative", className)}>
      <div className="relative h-80">
        <Banner
          title={
            isRtl
              ? (clientProjectDetails?.project?.arTitle ?? "")
              : (clientProjectDetails?.project?.enTitle ?? "")
          }
          image={
            clientProjectDetails?.project?.image
              ? getFilePath(clientProjectDetails?.project?.image?.key)
              : undefined
          }
          breadcrumbItems={[
            {
              name: isRtl
                ? (clientProjectDetails?.arTitle ?? "")
                : (clientProjectDetails?.enTitle ?? ""),
              href: "/projects",
            },
            {
              name: isRtl
                ? (clientProjectDetails?.project?.arTitle ?? "")
                : (clientProjectDetails?.project?.enTitle ?? ""),
            },
          ]}
        />
      </div>
      <div className="w-full h-full mb-20 mt-10 px-5 lg:px-0 ">
        <div className="container mx-auto grid lg:grid-cols-3 w-full gap-8">
          <div className="lg:col-span-2">
            <Flex direction="col" spacing={{ all: 8 }}>
              <Activity
                mode={
                  clientProjectDetails?.project?.image ? "visible" : "hidden"
                }
              >
                <div className="relative h-[445px] w-full">
                  <Image
                    src={getFilePath(
                      clientProjectDetails?.project?.image?.key!,
                    )}
                    alt={"project"}
                    fill
                    priority
                    placeholder="empty"
                    className="object-cover"
                  />
                </div>
              </Activity>
              <Flex direction="col" spacing={{ all: 4 }}>
                <SectionTitle
                  title={
                    isRtl
                      ? (clientProjectDetails?.project?.arTitle ?? "")
                      : (clientProjectDetails?.project?.enTitle ?? "")
                  }
                  subTitleTag="h3"
                  subTitle={
                    isRtl
                      ? (clientProjectDetails?.project?.arSubTitle ?? "")
                      : (clientProjectDetails?.project?.enSubTitle ?? "")
                  }
                  description={
                    isRtl
                      ? (clientProjectDetails?.project?.arDescription ?? "")
                      : (clientProjectDetails?.project?.enDescription ?? "")
                  }
                />
              </Flex>
              <Separator className="my-5" />
              <Activity
                mode={
                  clientProjectDetails?.gallery &&
                  clientProjectDetails?.gallery?.length > 0
                    ? "visible"
                    : "hidden"
                }
              >
                <Flex direction="col" spacing={{ all: 4 }}>
                  <SectionTitle
                    titleTag="h3"
                    title={t("titles.projectImages")}
                    description={t("paragraphs.projectImagesDescription")}
                  />

                  {clientProjectDetails?.gallery &&
                    clientProjectDetails?.gallery?.length > 0 && (
                      <GalleryContainer
                        images={
                          clientProjectDetails?.gallery?.map((image) => ({
                            path: image.image?.key ?? "",
                            alt: image.image?.alt ?? "",
                          })) ?? []
                        }
                      />
                    )}
                </Flex>
              </Activity>
            </Flex>
          </div>
          <ContactInfoBox
            image={
              clientProjectDetails?.project?.image
                ? getFilePath(clientProjectDetails?.project?.image?.key)
                : undefined
            }
            data={{
              client: clientProjectDetails?.client,
              socialMedia: clientProjectDetails?.socialMedia,
              periods: clientProjectDetails?.periods,
              covers: clientProjectDetails?.covers,
            }}
          />
        </div>
      </div>
    </section>
  );
};

export default ProjectDetailsContent;
